/**
 * @file MyBillingPage.tsx
 * @brief `/app/billing/:section`: a personal user's Billing area (plan, usage,
 *        cards, invoices), so they can pay on the web and the Mac app.
 *
 * @details
 * Built 17-10-2026 for the web/Mac launch: the phone's Plans / Billing /
 * Saved Cards screens had no web equivalent, and personal purchase UI is to
 * leave iOS/Android (App Store 3.1.1, payments audit fix #2). Each section is
 * a deep link (`/app/billing/cards`) so emails and notifications can open it.
 * A company member's billing is the company's: they get a short note instead
 * (company admins have /company/billing).
 *
 * @author DES
 * @date 17-10-2026
 */

import { CreditCard, Gauge, Receipt, Sparkles, type LucideIcon } from "lucide-react";
import { Navigate, NavLink, useParams } from "react-router-dom";
import { GridBand, GridBlock, GridCell, GridCells } from "@/features/analytics/components/GridBlock";
import { useAuth } from "@/shared/auth/auth-context";
import { ViewInvoicesLink } from "@/shared/billing/ViewInvoicesLink";
import { cn } from "@/shared/theme/cn";
import { billingAudienceFor } from "./billingErrors";
import { billingPath, isBillingSection, type BillingSection } from "./billingPaths";
import { CardsSection } from "./components/CardsSection";
import { InvoicesSection } from "./components/InvoicesSection";
import { PlanSection } from "./components/PlanSection";
import { UsageSection } from "./components/UsageSection";

const TABS: { id: BillingSection; label: string; icon: LucideIcon }[] = [
  { id: "plan", label: "Plan", icon: Sparkles },
  { id: "usage", label: "Usage", icon: Gauge },
  { id: "cards", label: "Cards", icon: CreditCard },
  { id: "invoices", label: "Invoices", icon: Receipt },
];

export function MyBillingPage() {
  const { section } = useParams();
  const { user } = useAuth();

  if (!isBillingSection(section)) return <Navigate to={billingPath("plan")} replace />;

  const audience = billingAudienceFor(user);
  if (audience !== "personal") {
    return (
      <GridBlock>
        <GridBand>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Billing</h2>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                {user?.company_name ? `${user.company_name} pays` : "Your company pays"} for your meetings and sessions. Cards,
                plans and invoices are managed by your company admin.
              </p>
            </div>
            {/* An admin who lands here gets the company's invoices (22-10-2026). */}
            {audience === "company_admin" ? <ViewInvoicesLink audience="company_admin" /> : null}
          </div>
        </GridBand>
      </GridBlock>
    );
  }

  return (
    <div className="pb-6">
      <GridBlock>
        <GridBand>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Billing</h2>
              <p className="mt-1 max-w-xl text-sm text-muted-foreground">Your plan, usage, cards and receipts.</p>
            </div>
            {section !== "invoices" ? <ViewInvoicesLink audience="personal" /> : null}
          </div>
        </GridBand>
        <GridCells className="grid-cols-2 sm:grid-cols-4" role="tablist" aria-label="Billing sections">
          {TABS.map((t) => (
            <GridCell key={t.id} padded={false} className="min-w-0">
              <NavLink
                to={billingPath(t.id)}
                role="tab"
                aria-selected={section === t.id}
                className={cn(
                  // No browser outline on click; a soft inset ring for the keyboard (21-10-2026).
                  "flex h-full min-h-[3.25rem] items-center gap-2.5 px-4 py-3 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/50",
                  section === t.id
                    ? "bg-brand-soft font-medium text-brand-soft-foreground"
                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                )}
              >
                <t.icon className="h-4 w-4 shrink-0" />
                {t.label}
              </NavLink>
            </GridCell>
          ))}
        </GridCells>
        {section === "plan" ? <PlanSection /> : null}
        {section === "usage" ? <UsageSection /> : null}
        {section === "cards" ? <CardsSection /> : null}
        {section === "invoices" ? <InvoicesSection /> : null}
      </GridBlock>
    </div>
  );
}
